import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, Loader2, Plus, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { AdvisorRecommendations } from "./AdvisorRecommendations";
import { useLang } from "../i18n/LanguageContext";
import { useStore } from "../context/StoreContext";
import { streamAdvisorChat, advisorRecommend, getProducts, formatEUR } from "../lib/api";
import { toast } from "sonner";

/* The advisor appends "[[SHOW:slug1,slug2]]" when it suggests concrete models.
   We strip the marker from the visible text and render product deal cards instead. */
const MARKER_RE = /\[\[SHOW:([^\]]*)\]\]/gi;

const parseAssistant = (content) => {
  const slugs = [];
  let m;
  const re = new RegExp(MARKER_RE.source, "gi");
  while ((m = re.exec(content)) !== null) {
    m[1].split(",").map((s) => s.trim().toLowerCase()).forEach((s) => {
      if (s && !slugs.includes(s)) slugs.push(s);
    });
  }
  let text = content.replace(new RegExp(MARKER_RE.source, "gi"), "").trimEnd();
  // hide a partially streamed marker at the end (e.g. "[[SHO")
  const pi = text.lastIndexOf("[[");
  if (pi !== -1 && text.indexOf("]]", pi) === -1) text = text.slice(0, pi).trimEnd();
  return { text, slugs: slugs.slice(0, 3) };
};

/* minimal rich text: render **bold** from the model nicely */
const renderRich = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : part
  );

const availTone = { in_stock: "text-mintx", low_stock: "text-amberx", preorder: "text-cyanx", out_of_stock: "text-destructive" };
const availKeyMap = { in_stock: "common.inStock", low_stock: "common.lowStock", preorder: "common.preorder", out_of_stock: "common.outStock" };

const ChatDealCard = ({ p, t, onAdd }) => (
  <div data-testid="chat-deal-card" data-slug={p.slug}
    className="flex items-center gap-3 rounded-xl card-ceramic p-2.5 hover:shadow-glow-cyan transition-shadow">
    <Link to={`/product/${p.slug}`} className="h-14 w-14 shrink-0 rounded-lg bg-black/40 hairline overflow-hidden">
      <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-contain p-1" />
    </Link>
    <div className="min-w-0 flex-1">
      <Link to={`/product/${p.slug}`} className="block font-display text-sm leading-tight truncate hover:text-cyanx transition-colors">{p.name}</Link>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className="font-display text-base">{formatEUR(p.price_eur)}</span>
        {p.compare_at_eur && <span className="text-[11px] text-muted-foreground line-through">{formatEUR(p.compare_at_eur)}</span>}
      </div>
      <span className={`text-[10px] ${availTone[p.availability] || "text-mintx"}`}>● {t(availKeyMap[p.availability] || "common.inStock")}</span>
    </div>
    <div className="flex flex-col gap-1.5 shrink-0">
      <Button data-testid="chat-deal-add" size="sm" onClick={() => onAdd(p)}
        aria-label={t("common.addToCart")} title={t("common.addToCart")}
        className="btn-glow border-0 h-8 w-8 p-0">
        <Plus className="h-4 w-4" />
      </Button>
      <Button asChild data-testid="chat-deal-view" size="sm" variant="secondary"
        aria-label={t("common.viewDetails")} title={t("common.viewDetails")}
        className="h-8 w-8 p-0 hairline">
        <Link to={`/product/${p.slug}`}><ArrowRight className="h-4 w-4" /></Link>
      </Button>
    </div>
  </div>
);

export const AdvisorChat = ({ layout = "split" }) => {
  const { t, lang } = useLang();
  const { addToCart } = useStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recs, setRecs] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [productMap, setProductMap] = useState({});
  const scrollRef = useRef(null);
  const sessionRef = useRef(`sess-${Math.random().toString(36).slice(2)}`);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, streaming]);

  useEffect(() => {
    getProducts({ lang }).then((d) => {
      const map = {};
      (d.products || []).forEach((p) => { map[p.slug] = p; });
      setProductMap(map);
    }).catch(() => {});
  }, [lang]);

  const addDeal = (p) => {
    addToCart({ slug: p.slug, name: p.name, price_eur: p.price_eur, image: p.image, kind: "product" });
    toast.success(`${p.name} \u2014 ${t("common.added")}`);
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    const next = [...messages, { role: "user", content }];
    setMessages([...next, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    try {
      await streamAdvisorChat(next, lang,
        (delta) => setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        }),
        null, sessionRef.current);
    } catch (e) {
      setMessages((prev) => { const c = [...prev]; c[c.length - 1] = { role: "assistant", content: "\u26A0 " + t("common.loading") }; return c; });
    } finally { setStreaming(false); }
  };

  const getRecs = async () => {
    const profile = messages.filter((m) => m.role === "user").map((m) => m.content).join(". ") || input;
    if (!profile.trim()) { send(t("advisor.startPrompt")); return; }
    setLoadingRecs(true);
    try {
      const data = await advisorRecommend(profile, lang, sessionRef.current);
      setRecs(data.result);
    } catch (e) { /* handled by UI */ } finally { setLoadingRecs(false); }
  };

  const chips = t("advisor.quickChips") || [];

  const ChatCol = (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[240px]">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-8">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl hairline shadow-glow-cyan"><Sparkles className="h-6 w-6 text-cyanx" /></span>
            <p className="text-sm text-muted-foreground max-w-xs">{t("advisor.subtitle")}</p>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {Array.isArray(chips) && chips.map((c, i) => (
                <button key={i} onClick={() => send(c)} className="chip hover:shadow-glow-cyan transition-shadow">{c}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === "user") {
            return (
              <div key={i} className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm bg-cyanx text-[#04121a]">{m.content}</div>
              </div>
            );
          }
          const { text, slugs } = parseAssistant(m.content);
          const deals = slugs.map((s) => productMap[s]).filter(Boolean);
          return (
            <div key={i} className="flex flex-col items-start gap-2">
              <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm card-ceramic text-foreground/90 whitespace-pre-wrap">
                {text ? renderRich(text) : (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
              </div>
              {deals.length > 0 && (
                <div data-testid="chat-deal-cards" className="w-full max-w-[92%] space-y-2 animate-fade-up">
                  {deals.map((p) => <ChatDealCard key={p.slug} p={p} t={t} onAdd={addDeal} />)}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 space-y-2">
        <div className="flex items-end gap-2">
          <Textarea data-testid="advisor-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t("advisor.placeholder")} rows={2}
            className="resize-none bg-[#0b0d12] border-border focus-visible:ring-cyanx" />
          <Button data-testid="advisor-send-button" onClick={() => send()} disabled={streaming} className="btn-glow border-0 h-10 w-10 p-0 shrink-0">
            {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        <Button data-testid="advisor-get-recs" onClick={getRecs} disabled={loadingRecs} variant="secondary" className="w-full hairline">
          {loadingRecs ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t("advisor.thinking")}</> : <><Sparkles className="h-4 w-4 mr-2 text-cyanx" />{t("advisor.getRecs")}</>}
        </Button>
      </div>
    </div>
  );

  if (layout === "stacked") {
    return (
      <div className="flex flex-col gap-4 h-full">
        <div className="flex-1 min-h-0">{ChatCol}</div>
        {(recs || loadingRecs) && <div className="border-t border-border pt-4 max-h-[45vh] overflow-y-auto">{loadingRecs ? <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-cyanx" /></div> : <AdvisorRecommendations result={recs} />}</div>}
      </div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6 h-full">
      <div className="rounded-2xl card-ceramic p-4 lg:h-[70vh]">{ChatCol}</div>
      <div className="rounded-2xl hairline p-4 lg:h-[70vh] overflow-y-auto bg-[#0b0d12]/40" data-testid="advisor-picks-panel">
        {loadingRecs ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-cyanx" /></div> : <AdvisorRecommendations result={recs} />}
      </div>
    </div>
  );
};
